import type { Theme } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { shadows } from './core/shadows';
import { palette } from './core/palette';
import { themeConfig } from './theme-config';
import { components } from './core/components';
import { typography } from './core/typography';
import { customShadows } from './core/custom-shadows';
import { mixins } from './core/mixins';
import type { ThemeOptions } from './types';
import { generateDarkModeShades, generateShades } from '../utils/colorUtils';
import { amber, red, blue, grey } from '@mui/material/colors';

export const baseTheme: ThemeOptions = {
    colorSchemes: {
        light: {
            palette: palette.light,
            shadows: shadows.light,
            customShadows: customShadows.light,
        },
        dark: {
            palette: palette.dark,
            shadows: shadows.dark,
            customShadows: customShadows.dark,
        },
    },
    mixins,
    components,
    typography,
    shape: { borderRadius: 8 },
    direction: themeConfig.direction,
    cssVariables: themeConfig.cssVariables,
    defaultColorScheme: themeConfig.enableSystemMode
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : themeConfig.defaultMode
};

export function createAppTheme(
    primaryColor: string,
    secondaryColor: string,
    mode: 'light' | 'dark'
): Theme {
    const selectedMode = mode || baseTheme.defaultColorScheme;
    if (typeof selectedMode !== 'string') {
        throw new Error('Mode must be a string.');
    }
    if (!baseTheme.colorSchemes || !baseTheme.colorSchemes[selectedMode]) {
        throw new Error(`Color scheme for mode '${selectedMode}' is not defined.`);
    }
    const schemePalette = baseTheme.colorSchemes[selectedMode].palette;
    // Dark paper is where primary-coloured text sits, so brand colours are tuned against it
    const darkSurface = palette.dark?.background?.paper as string;
    const brandShades = (hex: string) =>
        selectedMode === 'dark' ? generateDarkModeShades(hex, darkSurface) : generateShades(hex);
    const updatedTheme: ThemeOptions = {
        cssVariables: {
            colorSchemeSelector: 'data-toolpad-color-scheme',
        },
        palette: {
            ...schemePalette,
            mode: selectedMode,
            primary: brandShades(primaryColor),
            secondary: brandShades(secondaryColor),
        },
        mixins,
        customShadows: baseTheme.colorSchemes[selectedMode].customShadows,
        components,
        typography,
        shape: { borderRadius: 8 },
        direction: themeConfig.direction,
        defaultColorScheme: themeConfig.enableSystemMode
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : themeConfig.defaultMode
    };
    return createTheme(updatedTheme);
}

export function createStarWarsTheme(): Theme {
    const selectedMode = baseTheme.defaultColorScheme;
    if (typeof selectedMode !== 'string') {
        throw new Error('Mode must be a string.');
    }
    if (!baseTheme.colorSchemes || !baseTheme.colorSchemes[selectedMode]) {
        throw new Error(`Color scheme for mode '${selectedMode}' is not defined.`);
    }
    const updatedTheme: ThemeOptions = {
        cssVariables: {
            colorSchemeSelector: 'data-toolpad-color-scheme',
        },
        palette: {
            ...baseTheme.colorSchemes[selectedMode].palette,
            mode: 'dark',
            primary: amber,
            secondary: grey,
            error: red,
            success: blue,
            background: {
                default: '#000',
                paper: '#1c1c1c',
            },
            text: {
                primary: amber[500],
                secondary: grey[400],
            },
        },
        mixins,
        customShadows: baseTheme.colorSchemes[selectedMode].customShadows,
        components: {
            ...baseTheme.components,
            MuiButton: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#ff0000', // Sith Red
                        color: '#fff',
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        padding: '10px 20px',
                        textTransform: 'uppercase',
                        transition: '0.3s',
                        boxShadow: '0px 0px 10px #ff0000',
                        '&:hover': {
                            boxShadow: '0px 0px 20px #ff0000',
                        },
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        color: '#ff0000', // Sith glow
                    },
                    track: {
                        backgroundColor: '#0047ab', // Jedi blue
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#1c1c1c',
                        borderRadius: '12px',
                        border: '1px solid #ffcc00',
                        boxShadow: '0px 0px 10px #ffcc00',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: '#000',
                        color: '#ffcc00',
                        border: '1px solid #ffcc00',
                        boxShadow: '0px 0px 10px #ffcc00',
                    },
                },
            },
            MuiSnackbar: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#000',
                        border: '1px solid #ffcc00',
                        boxShadow: '0px 0px 10px #ffcc00',
                    },
                },
            },
        },
        typography: {
            ...baseTheme.typography,
            fontFamily: '"Star Jedi", Arial, sans-serif', // Star Wars-like font
            h1: {
                fontSize: '2.5rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
            },
            h2: {
                fontSize: '2rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
            },
            button: {
                fontWeight: 'bold',
                letterSpacing: 2,
            },
        },
        shape: { borderRadius: 8 },
        direction: themeConfig.direction,
        defaultColorScheme: themeConfig.enableSystemMode
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : themeConfig.defaultMode
    };
    return createTheme(updatedTheme);
}
